import DOMPurify from "dompurify";
import { fixInternalBlogLinks } from "./fixInternalLinks";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getBlogPost, useQuery } from "wasp/client/operations";
import { Badge } from "../client/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../client/components/ui/accordion";
import useTranslation from "../client/hooks/useTranslation";
import {
  buildFaqPageLd,
  serializeJsonLd,
} from "../shared/seoJsonLd";

type TocEntry = { id: string; text: string; level: number };
type FaqEntry = { question: string; answer: string };
type FaqBlock = { headingId: string; title: string; items: FaqEntry[]; trailing: string };

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

const TOC_KEYWORDS = ["sommaire", "table of contents", "table des matières"];
const FAQ_KEYWORDS = ["faq", "foire aux questions", "frequently asked"];

function isManualToc(el: Element): boolean {
  return TOC_KEYWORDS.some((kw) =>
    el.textContent?.trim().toLowerCase().startsWith(kw)
  );
}

function isFaqHeading(el: Element): boolean {
  return FAQ_KEYWORDS.some((kw) =>
    el.textContent?.trim().toLowerCase().includes(kw)
  );
}

type ProcessResult = {
  content: string;
  toc: TocEntry[];
  faqBlocks: FaqBlock[];
};

function processContent(html: string): ProcessResult {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const toc: TocEntry[] = [];
  const faqBlocks: FaqBlock[] = [];
  const slugCount: Record<string, number> = {};

  // Remove manually-written TOC: heading + following ordered list
  doc.querySelectorAll("h1, h2, h3, h4, p").forEach((el) => {
    if (!isManualToc(el)) return;
    const next = el.nextElementSibling;
    if (next && (next.tagName === "OL" || next.tagName === "UL")) {
      next.remove();
    }
    el.remove();
  });

  // Extract FAQ blocks: h2 with "faq" in text, followed by question/answer pairs.
  // Supports two TipTap formats:
  //   1. <h3>Question</h3><p>Answer</p>
  //   2. <p><strong>Question ?</strong> Answer in same paragraph</p>
  doc.querySelectorAll("h2").forEach((heading) => {
    if (!isFaqHeading(heading)) return;

    const title = heading.textContent?.trim() ?? "FAQ";
    const base = slugify(title) || "faq";
    slugCount[base] = (slugCount[base] ?? 0) + 1;
    const headingId = slugCount[base] === 1 ? base : `${base}-${slugCount[base]}`;

    const items: FaqEntry[] = [];
    const trailing: string[] = [];
    const toRemove: Element[] = [heading];

    let sibling = heading.nextElementSibling;
    while (sibling && sibling.tagName !== "H2") {
      const next = sibling.nextElementSibling;

      if (sibling.tagName === "H3") {
        // Format 1: h3 = question, following p = answer
        const question = sibling.textContent?.trim() ?? "";
        const answerParts: string[] = [];
        let answerEl = sibling.nextElementSibling;
        while (answerEl && answerEl.tagName !== "H3" && answerEl.tagName !== "H2") {
          answerParts.push(answerEl.innerHTML ?? "");
          toRemove.push(answerEl);
          answerEl = answerEl.nextElementSibling;
        }
        items.push({ question, answer: answerParts.join("") });
        toRemove.push(sibling);
        sibling = answerEl;
        continue;
      }

      if (sibling.tagName === "P") {
        // Format 2: <p><strong>Question ?</strong> Answer text</p>
        const strong = sibling.querySelector("strong");
        if (strong) {
          const question = strong.textContent?.trim() ?? "";
          // Answer = everything in the <p> after removing the <strong>
          const clone = sibling.cloneNode(true) as Element;
          clone.querySelector("strong")?.remove();
          const answer = clone.innerHTML.replace(/^\s*[–—-]?\s*/, "").trim();
          // Only treat as FAQ item if the strong text ends with "?"
          if (question && answer && question.endsWith("?")) {
            items.push({ question, answer });
            toRemove.push(sibling);
            sibling = next;
            continue;
          }
        }
        // Not a FAQ pair — keep as trailing content rendered after the accordion
        trailing.push(sibling.outerHTML);
        toRemove.push(sibling);
        sibling = next;
        continue;
      }

      toRemove.push(sibling);
      sibling = next;
    }

    if (items.length > 0) {
      faqBlocks.push({ headingId, title, items, trailing: trailing.join("") });
      // Replace the entire FAQ block with a placeholder div
      const placeholder = doc.createElement("div");
      placeholder.setAttribute("data-faq-id", headingId);
      heading.parentNode?.insertBefore(placeholder, heading);
      toRemove.forEach((el) => el.remove());
    }
  });

  // Index h2 headings for TOC (FAQ placeholders included by headingId)
  doc.querySelectorAll("h2").forEach((el) => {
    const text = el.textContent?.trim() ?? "";
    const base = slugify(text) || "section";
    slugCount[base] = (slugCount[base] ?? 0) + 1;
    const id = slugCount[base] === 1 ? base : `${base}-${slugCount[base]}`;
    el.setAttribute("id", id);
    toc.push({ id, text, level: 2 });
  });

  // Add FAQ blocks to TOC with their headingId
  faqBlocks.forEach((block) => {
    toc.push({ id: block.headingId, text: block.title, level: 2 });
  });

  return { content: doc.body.innerHTML, toc, faqBlocks };
}

// Splits the processed HTML on data-faq-id placeholders so FAQ blocks can be
// rendered as React accordions inline in the article flow.
type ContentSegment =
  | { type: "html"; html: string }
  | { type: "faq"; faqId: string };

function splitSegments(html: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  const placeholderRe = /<div data-faq-id="([^"]+)"><\/div>/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = placeholderRe.exec(html)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "html", html: html.slice(lastIndex, match.index) });
    }
    segments.push({ type: "faq", faqId: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < html.length) {
    segments.push({ type: "html", html: html.slice(lastIndex) });
  }

  return segments;
}

const PROSE =
  "prose prose-invert max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary prose-strong:text-foreground prose-code:text-primary";

export default function BlogPostPage() {
  const t = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading, error } = useQuery(getBlogPost, { slug: slug! });

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="h-8 w-48 bg-card animate-pulse rounded mb-4" />
        <div className="h-64 bg-card animate-pulse rounded-xl mb-8" />
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-4 bg-card animate-pulse rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <p className="text-muted-foreground">{t.blog.postNotFound}</p>
        <Link to="/blog" className="text-primary hover:underline mt-4 inline-block">
          {t.blog.backToBlog}
        </Link>
      </div>
    );
  }

  const formattedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const rawTitle = post.metaTitle ?? post.title;
  // Mirror buildBlogPostHead (SSR): don't double-append the brand.
  const pageTitle = /\bedgely\b/i.test(rawTitle) ? rawTitle : `${rawTitle} — Edgely`;
  const pageDescription = post.metaDescription ?? post.excerpt ?? "";
  const coverImageUrl: string | null = post.coverImageUrl ?? null;

  const sanitizedContent = fixInternalBlogLinks(
    DOMPurify.sanitize(post.content, {
      ADD_ATTR: ["style", "class", "data-faq-id"],
      ADD_TAGS: ["iframe"],
    }),
  );

  const { content: processedContent, toc, faqBlocks } = processContent(sanitizedContent);
  const faqMap = new Map(faqBlocks.map((b) => [b.headingId, b]));
  const segments = splitSegments(processedContent);

  const faqItems = faqBlocks.flatMap((b) => b.items).map((it) => ({
    question: it.question.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(),
    answer: it.answer.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(),
  }));
  const faqLd = buildFaqPageLd(faqItems);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={post.metaTitle ?? post.title} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://edgely.trade/blog/${post.slug}`} />
        <meta property="og:image" content={coverImageUrl ?? "https://edgely.trade/public-banner.webp"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle ?? post.title} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={coverImageUrl ?? "https://edgely.trade/public-banner.webp"} />
        <link rel="canonical" href={`https://edgely.trade/blog/${post.slug}`} />
        {faqLd && (
          <script type="application/ld+json">{serializeJsonLd(faqLd)}</script>
        )}
      </Helmet>

      <div className="min-h-screen bg-background">
        {coverImageUrl && (
          <div className="w-full max-h-[480px] overflow-hidden">
            <img
              src={coverImageUrl}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="max-w-3xl mx-auto px-4 py-10">
          <Link
            to="/blog"
            className="text-sm text-muted-foreground hover:text-primary transition-colors mb-6 inline-block"
          >
            {t.blog.backToBlog}
          </Link>

          <div className="mb-8">
            {post.category && (
              <div className="mb-3">
                <Badge variant="secondary">{post.category.name}</Badge>
              </div>
            )}
            <h1 className="text-3xl font-bold text-foreground mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span>
                {t.blog.by} {post.authorName}
              </span>
              {formattedDate && (
                <>
                  <span>·</span>
                  <span>
                    {t.blog.publishedOn} {formattedDate}
                  </span>
                </>
              )}
            </div>
          </div>

          {toc.length > 1 && (
            <nav className="mb-8 rounded-lg border border-border bg-card p-4">
              <p className="text-sm font-semibold text-foreground mb-3">
                {t.blog.tableOfContents}
              </p>
              <ol className="space-y-1.5 list-decimal list-inside">
                {toc.map((entry) => (
                  <li key={entry.id}>
                    <a
                      href={`#${entry.id}`}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {entry.text}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {segments.map((seg, i) => {
            if (seg.type === "html") {
              return (
                <div
                  key={i}
                  className={PROSE}
                  dangerouslySetInnerHTML={{ __html: seg.html }}
                />
              );
            }

            const block = faqMap.get(seg.faqId);
            if (!block) return null;

            return (
              <div key={i} id={block.headingId} className="mt-10 mb-6">
                <h2 className="text-xl font-bold text-foreground mb-4">
                  {block.title}
                </h2>
                <Accordion type="single" collapsible className="w-full space-y-3">
                  {block.items.map((item, idx) => (
                    <AccordionItem
                      key={idx}
                      value={`faq-${block.headingId}-${idx}`}
                      className="border-border hover:bg-muted/20 rounded-lg border px-6 py-1 transition-all duration-200"
                    >
                      <AccordionTrigger className="text-foreground hover:text-primary text-left text-base font-semibold hover:no-underline transition-colors duration-200">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div
                          className="text-muted-foreground text-sm leading-7"
                          dangerouslySetInnerHTML={{ __html: item.answer }}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                {block.trailing && (
                  <div
                    className={`mt-4 ${PROSE}`}
                    dangerouslySetInnerHTML={{ __html: block.trailing }}
                  />
                )}
              </div>
            );
          })}

          {post.tags.length > 0 && (
            <div className="mt-10 pt-6 border-t border-border flex flex-wrap gap-2">
              {post.tags.map((tag: string) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
